import React, {ReactNode} from "react";
import styled from "styled-components";
import Icon from "../Icon/Icon";
import ImageCrop from "../UI/Image/ImageCrop";
import Anchor from "../../../components/Router/Anchor";
import {Venue} from "../../../typings/document-types";
import format from "../../../lib/date/format";
import isValid from "../../../lib/date/isValid";
import {Device} from "../../../components/device";
import {AppThunkDispatch} from "../../../store";
import {showTicketsAsync} from "../../../store/basm/action";
import {connect} from "react-redux";

//Usage:
/*
<Poster
    image="https://via.placeholder.com/1600x2000"
    header="Headline"
    href="#"
    target="_self"
    showHeader
    venue="Stort Scene"
    date="Fra 31. Jan"
    buyBtn>
    <Text>
        <h4>Header</h4>
    </Text>
</Poster>
*/

//To do: buy btn functionality

const PosterWrap = styled.div`
    border: none;
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: 0;
    position: relative;
    z-index: 0;
    white-space: initial;
    padding-bottom: 35px;
    transform: scale(1);
    transition: all .4s;
    margin-bottom: 1rem;

    ${Device.laptop} {
        margin-bottom: 2rem;
    }

    &:hover {
        box-shadow: var(--shadow-md);
        transform: scale(1.025);
    }

    .poster__link {
        display: block;
        padding-top: 125%;
        width: 100%;
        position: relative;
    }

    .poster__image {
        display: block;
        top: 0;
        overflow: hidden;
        position: absolute;
        height: 100%;
        width: 100%;
    }
`;

const PosterText = styled.div`
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    padding: 1.25rem 1.8rem 0.5rem 1.8rem;
    background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.5) 100%);
    color: var(--color-white, white);
    font-weight: normal;
    text-align: left;
    text-shadow: 0 0 63px rgba(0, 0, 0, .3);
    text-transform: none;

    p {
        margin-bottom: 1rem;
    }
`;

const PosterInfo = styled.div`
    background-color: var(--color-black, black);
    color: var(--color-gray-20, white);
    text-transform: uppercase;
    padding: .5rem .5rem .5rem 1.8rem;
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    min-height: 35px;
    font-size: 0.7333rem;
    font-weight: bold;
    letter-spacing: 0.1em;
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: center;

    .poster__icon {
        position: absolute;
        left: 10px;
        top: 0;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .poster__venue,
    .poster__date {
        margin-right: .5rem;
    }

    > *:last-child {
        align-self: flex-end;
        justify-self: flex-end;
        margin-left: auto;
        margin-right: 0;
    }
`;

interface PosterProps {
    image?: string;
    href?: string;
    target?: string;
    imageAlt?: string;
    showHeader?: boolean;
    venues?: Venue[];
    date?: Date;
    children?: ReactNode;
    eventNo?: number;
    showNo?: number;
    isSoldOut: boolean;
    events: any;
    buyOnClick?: (eventNo: number, showNo?: number) => void;
    purchaseUrls?: any;
    previewTeaserText?: string;
}

const Poster: React.FC<PosterProps & StateProps> = (props) => {
    const firstVenue = props.venues && props.venues.length > 0 ? props.venues[0] : null;

    const handleTixlyTicketClicked = (purchaseUrls) => {
        window.open(purchaseUrls[0].link ? purchaseUrls[0].link : purchaseUrls[0].Link, "_self");
    };

    const getIcon = () => {
        if (firstVenue) {
            switch (firstVenue.name) {
                case "Scala":
                    return <span className="poster__icon"><Icon name="scala" className="w-3 h-3"/></span>;
                case "Store Scene":
                    return <span className="poster__icon"><Icon name="store-scene" className="w-3 h-3"/></span>;
                case "Scala Foyer":
                    return <span className="poster__icon"><Icon name="scala" className="w-3 h-3"/></span>;
                case "Studio":
                    return <span className="poster__icon"><Icon name="studio" className="w-3 h-3"/></span>;
                case "Stiklingen":
                    return <span className="poster__icon"><Icon name="stiklingen" className="w-3 h-3"/></span>;
                case "Byen":
                case "Ude i byen":
                    return <span className="poster__icon"><Icon name="pil" className="w-4 h-4"/></span>;
                case "Kampmann Salen":
                case "Otto Mønsted Salonen":
                    return <span className="poster__icon"><Icon name="circle" className="w-4 h-4"/></span>;
            }
        }
    };

    const ImageLink = (props) => {
        const {to, target, ...rest} = props;
        if (to) {
            return <Anchor to={to} target={target} {...rest} />;
        }
        return <div {...rest} />;
    };

    return (
        <PosterWrap>
            <ImageLink to={props.href || ""} target={props.target} className="poster__link">
                {props.image ?
                    <ImageCrop src={props.image} width="100%" heightRatio={5 / 4} resizeMode="crop" alt={props.imageAlt}
                               className="poster__image"/> : null}
                {props.children ? <PosterText>{props.children}</PosterText> : null}
            </ImageLink>
            <PosterInfo>
                {getIcon()}
                <span className="poster__venue">{firstVenue ? firstVenue.name : ""}</span>
                {!props.previewTeaserText ? <span
                        className="poster__date">{props.date && isValid(props.date) ? `FRA ${format(props.date, "d. MMM")}` : null}</span> :
                    <span className="poster__date">{props.previewTeaserText} </span>}
                {props.isSoldOut ? <span>Udsolgt</span> : props.eventNo ? (
                    <button onClick={() => handleTixlyTicketClicked(props.purchaseUrls)}>KØB</button>
                ) : null}
            </PosterInfo>
        </PosterWrap>
    );
};

const mapToDispatchProps = (dispatch: AppThunkDispatch) =>
    ({
        showTickets: (eventNo: number, showNo?: number) => dispatch(showTicketsAsync(eventNo, showNo))
    });

type StateProps = ReturnType<typeof mapToDispatchProps>;

export default connect(null, mapToDispatchProps)(Poster);